import { EventsWidgetContextProvider } from "./components/EventsContext";
import { Flex, Grid } from "styled-system/jsx";
import { FilterCarousel } from "./components/FilterCarousel";
import { MonthYearSelector } from "./components/MonthYearSelector";
import { DateRangeSelector } from "./components/DateRangeSelector";
import { RecurringEvents } from "./components/RecurringEvents";
import { FeaturedEvents } from "./components/FeaturedEvents";
import { useI18NContext } from "widgets/shared";
import { Spinner } from "@ttc3k/trekker";
import { eventsWidgetRecipe } from "./EventsWidget.recipe";
import { EventWidgetProvidersProps } from "./EventWidgetProviders";
import { i18nLanguage } from "i18n";
import { useRef } from "react";
import { useEventsWidget, useWidgetResizeObserver } from "./hooks";
import { RenderDetailsSidePane } from "shared/components/organisms/RenderDetailsSidePane/RenderDetailsSidePane";
import { EventsCarousel } from "./components/EventsCarousel/EventsCarousel";
import { EmptyState } from "shared/components/root/EmptyState";
import { useTranslation } from "react-i18next";

export type EventsWidgetDetailsDisplayMode = "link" | "slideout";
export type EventsWidgetListDisplayMode = "grid" | "carousel";

type EventsWidgetProps = Omit<
  EventWidgetProvidersProps,
  "children" | "fontFamilyOverride"
> & {
  hostElement?: HTMLElement & {
    carouselEventsLoaded?: (e: CustomEvent) => void;
  };
  widgetDisplayMode?: EventsWidgetListDisplayMode; // Will be overridden, so should use displayMode
  hideFilters?: boolean;
  applyFilters?: string[]; // Will be overridden, so should use eventTags
  nextNMonths?: number; // Will be overridden, so should use lookAheadMonths
  eventDetailsLink?: string;
  eventDetailsDisplayMode?: EventsWidgetDetailsDisplayMode;
  moreEventsLink?: string;
  hideCarouselScrollbar?: boolean;
  carouselNoEventsText?: string;
  viewOnly?: boolean;
};

const EventsWidget = ({
  hostElement,
  organizationId,
  widgetId,
  language = i18nLanguage.en,
  widgetDisplayMode = "grid", // Will be overridden, so should use displayMode
  hideFilters,
  applyFilters = [], // Will be overridden, so should use eventTags
  nextNMonths = 0, // Will be overridden, so should use lookAheadMonths
  eventDetailsLink,
  eventDetailsDisplayMode = "link",
  moreEventsLink,
  hideCarouselScrollbar = false,
  carouselNoEventsText,
  viewOnly = false,
}: EventsWidgetProps) => {
  const { loading: loadingLanguages } = useI18NContext();
  const { t } = useTranslation("events", { keyPrefix: "WIDGET.ERRORS" });

  const {
    isInvalidWidget,
    displayMode, // Use this instead of widgetDisplayMode below here
    eventTags, // Use this instead of applyFilters below here
    lookAheadMonths, // Use this instead of nextNMonths below here
    loadingWidgetDetails,
    loadingMonthsAndEventCounts,
    dateRange,
    featuredEvents,
    recurringEvents,
    error,
    loading,
    filterTags,
    selectedFilterTag,
    yearGroups,
    firstMonthOfYearWithEvents,
    calendarMaxDate,
    calendarMinDate,
    calendarSkippedDates,
    handleFilterChange,
    handleDateRangeChange,
    handleSelectedDatesChange,
  } = useEventsWidget({
    hostElement,
    organizationId,
    widgetId,
    lang: language,
    widgetDisplayMode, // After this usage, use displayMode instead
    applyFilters, // After this usage, use eventTags instead
    nextNMonths, // After this usage, use lookAheadMonths instead
  });

  // If hideFilters is not provided, default to true if the display mode is carousel
  hideFilters = hideFilters ?? displayMode === "carousel";

  /**
   * Watch for changes to the width of the embedded widget because the
   * layout changes based on its size (as opposed to the screen size)
   * - For the main layout, only the featured events/recurring events
   *   grid will change based on the widget width
   */
  const widgetContainerRef = useRef<HTMLDivElement | null>(null);
  const { widgetSize, widgetHeaderSize } = useWidgetResizeObserver(
    widgetContainerRef,
    dateRange,
  );

  const classes = eventsWidgetRecipe({
    size: widgetSize,
    view:
      dateRange.isFullMonth &&
      !dateRange.forceGridView &&
      !dateRange.showFutureEventNotice &&
      displayMode !== "carousel"
        ? "month"
        : "selectedDates",
  });

  if (loadingLanguages || loadingWidgetDetails) {
    return (
      <Flex
        w={"full"}
        h={"full"}
        justifyContent={"center"}
        alignItems={"center"}
      >
        <Spinner width={"40px"} height={"40px"} />
      </Flex>
    );
  }

  if (isInvalidWidget) {
    return (
      <EmptyState
        part="events__invalid-widget"
        text={t("INVALID_WIDGET")}
        h="full"
        minH="364px"
        backgroundColor="bg.lightest"
      />
    );
  }

  return (
    <EventsWidgetContextProvider
      organizationId={organizationId}
      lang={language}
      widgetSize={widgetSize}
      widgetHeaderSize={widgetHeaderSize}
      widgetDisplayMode={displayMode}
      hideFilters={hideFilters}
      applyFilters={eventTags}
      nextNMonths={lookAheadMonths}
      eventDetailsLink={eventDetailsLink}
      eventDetailsDisplayMode={eventDetailsDisplayMode}
      moreEventsLink={moreEventsLink}
      hideCarouselScrollbar={hideCarouselScrollbar}
      viewOnly={viewOnly}
    >
      <Flex
        ref={widgetContainerRef}
        flexDir={"column"}
        mx={"auto"}
        alignItems={"center"}
        width={"full"}
        gap="400"
      >
        {lookAheadMonths === 0 && !hideFilters && (
          <Flex flexDir="column" gap="250" w="full">
            {lookAheadMonths === 0 && (
              /* Month-Year and Date selectors */
              <Flex
                flexDir="row"
                justifyContent={"space-between"}
                alignItems={"center"}
                gap="200"
                w="full"
                minH="42px"
              >
                <MonthYearSelector
                  dateRange={dateRange}
                  lang={language}
                  loading={loading}
                  loadingMonthsAndEventCounts={loadingMonthsAndEventCounts}
                  yearGroups={yearGroups}
                  firstMonthOfYearWithEvents={firstMonthOfYearWithEvents}
                  onDateRangeChange={handleDateRangeChange}
                />
                <DateRangeSelector
                  dateRange={dateRange}
                  calendarMaxDate={calendarMaxDate}
                  calendarMinDate={calendarMinDate}
                  calendarSkippedDates={calendarSkippedDates}
                  onSelectedDatesChange={handleSelectedDatesChange}
                />
              </Flex>
            )}

            {/* Tag selector */}
            <FilterCarousel
              tags={filterTags}
              selectedTag={selectedFilterTag}
              onTagSelect={handleFilterChange}
            />
          </Flex>
        )}

        {displayMode !== "carousel" ? (
          /* Events and recurring events */
          <Grid part="events__container" className={classes.events}>
            {/* Featured events and pagination buttons */}
            <FeaturedEvents
              events={featuredEvents}
              loading={
                loading || loadingWidgetDetails || loadingMonthsAndEventCounts
              }
              error={error}
              dateRange={dateRange}
            />
            {/* Recurring events */}
            {dateRange.isFullMonth &&
              !dateRange.forceGridView &&
              !dateRange.showFutureEventNotice && (
                <RecurringEvents
                  events={recurringEvents}
                  loading={
                    loading ||
                    loadingWidgetDetails ||
                    loadingMonthsAndEventCounts
                  }
                />
              )}
          </Grid>
        ) : (
          <EventsCarousel
            events={featuredEvents}
            loading={
              loading || loadingWidgetDetails || loadingMonthsAndEventCounts
            }
            carouselNoEventsText={carouselNoEventsText}
          />
        )}
      </Flex>

      <RenderDetailsSidePane sidePaneType="events" viewOnly={viewOnly} />
    </EventsWidgetContextProvider>
  );
};

export default EventsWidget;
